import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// Define routes
const routes = [
  {
    path: '/fuel-card-management',
    name: 'fuel-card-management',
    component: () => import('@/views/FuelCardManagement.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/Home.vue')
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/Dashboard.vue'),
    meta: { requiresAuth: true }
  },


  {
    path: '/drivers',
    name: 'drivers',
    component: () => import('@/views/Drivers.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/vehicles',
    name: 'vehicles',
    component: () => import('@/views/Vehicles.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/auth/login',
    name: 'login',
    component: () => import('@/views/auth/Login.vue'),
    meta: { guestOnly: true }
  },
  {
    path: '/auth/register',
    name: 'register',
    component: () => import('@/views/auth/Register.vue'),
    meta: { guestOnly: true }
  },
  {
    path: '/auth/verify-email',
    name: 'verify-email',
    component: () => import('@/views/auth/VerifyEmail.vue'),
    meta: { guestOnly: true }
  },
  {
    path: '/auth/callback',
    name: 'auth-callback',
    component: () => import('@/views/auth/Callback.vue')
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/views/Profile.vue'),
    meta: { requiresAuth: true }
  },
  // Company Management Routes
  {
    path: '/company',
    name: 'company',
    component: () => import('@/views/Company.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/head-office',
    name: 'head-office',
    component: () => import('@/views/HeadOffice.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/depots',
    name: 'depots',
    component: () => import('@/views/Depots.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/locations-map',
    name: 'locations-map',
    component: () => import('@/views/LocationsMap.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/tanks',
    name: 'tanks',
    component: () => import('@/views/Tanks.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/tank-journal',
    name: 'tank-journal',
    component: () => import('@/views/TankJournal.vue'),
    meta: { requiresAuth: true }
  },
  // Reports route with children for each report type
  {
    path: '/reports',
    name: 'reports',
    component: () => import('@/views/reports/ReportLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'reports-dashboard',
        component: () => import('@/views/reports/ReportsDashboard.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'fuel',
        name: 'fuel-reports',
        component: () => import('@/views/reports/FuelReports.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'financial',
        name: 'financial-reports',
        component: () => import('@/views/reports/FinancialReports.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'vehicles',
        name: 'vehicle-reports',
        component: () => import('@/views/reports/VehicleReports.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'audit-log',
        name: 'audit-log-report',
        component: () => import('@/views/reports/AuditLogReport.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'template',
        name: 'reports-template',
        component: () => import('@/views/reports/1main/reportstemplate.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'absa-reconciliation',
        name: 'absa-reconciliation-report',
        component: () => import('@/views/reports/financial/AbsaReconciliationReport.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'fuel-receipt',
        name: 'fuel-receipt-report',
        component: () => import('@/views/reports/1main/5tankreport/FuelReceiptReport.vue'),
        meta: { requiresAuth: true }
      },
      // Vehicle Reports (1vehicle)
      {
        path: 'consumption-per-vehicle',
        name: 'consumption-per-vehicle-report',
        component: () => import('@/views/reports/1main/1vehicle/ConsumpPerVehicle.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'fuel-use-per-vehicle',
        name: 'fuel-use-per-vehicle-report',
        component: () => import('@/views/reports/1main/1vehicle/FuelUsePerVehicle.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'benchmark-by-asset-type',
        name: 'benchmark-by-asset-type-report',
        component: () => import('@/views/reports/1main/1vehicle/BenchmarkByAssetType.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'fuel-consumption-driver',
        name: 'fuel-consumption-driver-report',
        component: () => import('@/views/reports/1main/1vehicle/FuelConsumpDriver.vue'),
        meta: { requiresAuth: true }
      },
      // Transaction Reports (2transaction)
      {
        path: 'absa-recon',
        name: 'absa-recon-report',
        component: () => import('@/views/reports/1main/2transaction/AbsaRecon.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'month-recon',
        name: 'month-recon-report',
        component: () => import('@/views/reports/1main/2transaction/MonthRecon.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'transaction-contract-depot',
        name: 'transaction-contract-depot-report',
        component: () => import('@/views/reports/1main/2transaction/TransactContractDepotReport.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'contractor-markup',
        name: 'contractor-markup-report',
        component: () => import('@/views/reports/1main/2transaction/ContractorMarkupReport.vue'),
        meta: { requiresAuth: true }
      },
      // Driver/Vehicle Reports (3drivervehicle)
      {
        path: 'vehicle-reports',
        name: 'vehicle-reports',
        component: () => import('@/views/reports/1main/3drivervehicle/VehicleReports.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'contract-reports',
        name: 'contract-reports',
        component: () => import('@/views/reports/1main/3drivervehicle/ContractReports.vue'),
        meta: { requiresAuth: true }
      },
      // Operations Reports (4operations)
      {
        path: 'tank-variance',
        name: 'tank-variance-report',
        component: () => import('@/views/reports/1main/4operations/TankVarianceReport.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'audit-log',
        name: 'audit-log-report',
        component: () => import('@/views/reports/1main/4operations/AuditLogReport.vue'),
        meta: { requiresAuth: true }
      },
      // Tank Reports (5tankreport)
      {
        path: 'tank-detailed-variance',
        name: 'tank-detailed-variance-report',
        component: () => import('@/views/reports/1main/5tankreport/TankDetailedVarianceReport.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'tank-recon-monthly',
        name: 'tank-recon-monthly-report',
        component: () => import('@/views/reports/1main/5tankreport/TankReconMonthly.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'tank-variance',
        name: 'tank-variance-report',
        component: () => import('@/views/reports/1main/4operations/TankVarianceReport.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'monthly-recon',
        name: 'monthly-recon-report',
        component: () => import('@/views/reports/MonthlyReconReport.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'sub-contractors',
        name: 'sub-contractors-report',
        component: () => import('@/views/reports/SubContractorsReport.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'transactions',
        name: 'transactions-report',
        component: () => import('@/views/reports/TransactionsReport.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'fuel-usage',
        name: 'fuel-usage-report',
        component: () => import('@/views/reports/FuelUsageReport.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'driver-performance',
        name: 'driver-performance-report',
        component: () => import('@/views/reports/drivers/DriverPerformanceReport.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'vehicle-maintenance',
        name: 'vehicle-maintenance-report',
        component: () => import('@/views/reports/vehicles/VehicleMaintenanceReport.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'contract-depot',
        name: 'contract-depot-report',
        component: () => import('@/views/reports/ContractDepotReport.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'comprehensive-refueling',
        name: 'comprehensive-refueling-report',
        component: () => import('@/views/reports/ulti/UltiReport.vue'),
        meta: { requiresAuth: true }
      }
    ]
  },
  {
    path: '/contracts',
    name: 'contracts',
    component: () => import('@/views/contracts/Contracts.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/contracts/vcontracts',
    name: 'vehicle-contracts',
    component: () => import('@/views/contracts/VContracts.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/price-tracking',
    name: 'PriceTracking',
    component: () => import('@/views/PriceTracking.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/divisions',
    name: 'divisions',
    component: () => import('@/views/Divisions.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/users',
    name: 'users',
    component: () => import('@/views/Users.vue'),
    meta: { requiresAuth: true }
  },

  {
    path: '/test-drivers',
    name: 'test-drivers',
    component: () => import('@/views/TestDrivers.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/sub-contractors',
    name: 'sub-contractors',
    component: () => import('@/views/SubContractors.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/subcon-fleet',
    name: 'subcon-fleet',
    component: () => import('@/views/SubConFleet.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/fuel-management',
    name: 'fuel-management',
    component: () => import('@/views/FuelManagement.vue'),
    meta: { requiresAuth: true }
  },

  {
    path: '/admin/card-refuel-transactions',
    name: 'admin-card-refuel-transactions',
    component: () => import('@/views/admin/CardRefuelTransactions.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/admin/driver-authentication',
    name: 'admin-driver-authentication',
    component: () => import('@/views/admin/DriverAuthentication.vue'),
    meta: { requiresAuth: true }
  },
  // Fuel Consumption Route
  {
    path: '/fuel-consumption',
    name: 'fuel-consumption',
    component: () => import('@/views/refueling/FuelConsumption.vue'),
    meta: { requiresAuth: true }
  },
  // Refueling Routes
  {
    path: '/refueling/card-refuel',
    name: 'card-refuel',
    component: () => import('@/views/refueling/CardRefuel.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/refueling/own-refuel',
    name: 'own-refuel',
    component: () => import('@/views/refueling/OwnRefuel.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/refueling/attendant-third-party-refuel',
    name: 'attendant-third-party-refuel',
    component: () => import('@/views/refueling/AttendantThirdPartyRefuel.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/refueling/tank-refilling',
    name: 'tank-refilling',
    component: () => import('@/views/refueling/TankRefilling.vue'),
    meta: { requiresAuth: true }
  },
  // OPS Profile Route
  {
    path: '/ops/profile',
    name: 'ops-profile',
    component: () => import('@/views/ops/Profile.vue'),
    meta: { requiresAuth: true }
  },
  // OPS Refueling Routes
  {
    path: '/ops/card-refuel',
    name: 'ops-card-refuel',
    component: () => import('@/views/ops/CardRefuel.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/ops/attendant-refuel',
    name: 'ops-attendant-refuel',
    component: () => import('@/views/ops/AttendantRefuel.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/ops/tank-refilling',
    name: 'ops-tank-refilling',
    component: () => import('@/views/ops/TankRefilling.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/ops/subcontractor-refuel',
    name: 'ops-subcontractor-refuel',
    component: () => import('@/views/ops/SubcontractorRefuel.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/ops/tank-recon',
    name: 'ops-tank-recon',
    component: () => import('@/views/ops/TankRecon.vue'),
    meta: { requiresAuth: true }
  },


  // Add-Ons Routes
  {
    path: '/maintenance',
    name: 'maintenance',
    component: () => import('@/views/Maintenance.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/tire-maintenance',
    name: 'tire-maintenance',
    component: () => import('@/views/TireMaintenance.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/gps-tracking',
    name: 'gps-tracking',
    component: () => import('@/views/GpsTracking.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/admin-api',
    name: 'admin-api',
    component: () => import('@/views/AdminAPI.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/admin/api-management',
    name: 'admin-api-management',
    component: () => import('@/views/admin/AdminApiManagement.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/admin/fuel-tank-reconciliation',
    name: 'admin-fuel-tank-reconciliation',
    component: () => import('@/components/admin/FuelTankReconciliation.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/reports/daily-discrepancy',
    name: 'daily-discrepancy-report',
    component: () => import('@/views/reports/DailyDiscrepancyReport.vue'),
    meta: { requiresAuth: true }
  },
  // Catch-all route for 404
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFound.vue')
  }
]

// Create router instance
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Force re-initialization of auth state on each navigation
  await authStore.initAuth()

  const isAuthenticated = authStore.isAuthenticated
  const userRole = authStore.user?.user_metadata?.role;

  // For debugging - log authentication state
  console.log('Auth state:', {
    isAuthenticated,
    userRole,
    path: to.path
  })

  // Skip auth checks for callback route
  if (to.name === 'auth-callback') {
    return next()
  }

  // Driver role restriction: if logged in as a driver, only allow access to the card refuel page.
  if (isAuthenticated && userRole === 'driver' && to.name !== 'ops-card-refuel') {
    console.log('Driver trying to access restricted page:', to.path, '. Redirecting to card refuel.');
    return next({ name: 'ops-card-refuel' });
  }

  // Protected routes: if not authenticated, redirect to login.
  if (to.meta.requiresAuth && !isAuthenticated) {
    console.log('Redirecting to login from protected route:', to.path)
    return next({ name: 'login', query: { redirect: to.fullPath } })
  }

  // Guest-only routes: if authenticated, redirect away from login/register.
  if (to.meta.guestOnly && isAuthenticated) {
    // Redirect fuel attendants to OPS section
    if (userRole === 'fuel_attendant') {
      console.log('Redirecting fuel attendant to OPS section from guest-only route:', to.path)
      return next({ name: 'ops-card-refuel' })
    }
    // Non-driver users are redirected to the dashboard.
    // Drivers are already handled by the rule above, but this is a good fallback.
    console.log('Redirecting to dashboard from guest-only route:', to.path)
    return next({ name: 'dashboard' })
  }

  // Redirect fuel attendants from dashboard/home to OPS section
  if (isAuthenticated && userRole === 'fuel_attendant' && (to.name === 'dashboard' || to.name === 'home')) {
    console.log('Redirecting fuel attendant from dashboard/home to OPS section')
    return next({ name: 'ops-card-refuel' })
  }

  // Restrict fuel attendants to OPS routes only
  const opsAllowedRoutes = [
    // OPS section route names
    'ops-profile',
    'ops-card-refuel',
    'ops-attendant-refuel',
    'ops-tank-refilling',
    'ops-subcontractor-refuel',
    'ops-tank-recon',
    // Optionally allow logout/profile
    'logout'
  ]
  if (
    userRole === 'fuel_attendant' &&
    to.meta.requiresAuth &&
    !opsAllowedRoutes.includes(String(to.name))
  ) {
    // Redirect attendants to their OPS dashboard
    console.log('Fuel attendant tried to access non-OPS route:', to.path)
    next({ name: 'ops-card-refuel' })
    return
  }

  // Restrict drivers to only card refuel access
  const driverAllowedRoutes = [
    // Driver allowed routes
    'ops-card-refuel',
    'ops-profile',
    // Optionally allow logout/profile
    'logout',
    'profile'
  ]
  if (
    userRole === 'driver' &&
    to.meta.requiresAuth &&
    !driverAllowedRoutes.includes(String(to.name))
  ) {
    // Redirect drivers to card refuel page
    console.log('Driver tried to access non-allowed route:', to.path)
    next({ name: 'ops-card-refuel' })
    return
  }

  next()
})

export default router
